import React from "react";
import { toast } from "react-toastify";
import {
    IMPERSONATION_STOP_ABANDONED_EVENT,
    ImpersonationService,
    RECORDING_IN_OTHER_TAB_ERROR,
} from "../lib/impersonationService";
import { ImpersonationSession } from "../utils/impersonation.types";
import AlertTriangleThinIcon from "@assets/hd-icons/alert-triangle-thin.svg?react";

interface ImpersonationBannerProps {
    className?: string;
}

const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({ className = "" }) => {
    const [impersonationSession, setImpersonationSession] = React.useState<ImpersonationSession | null>(null);

    React.useEffect(() => {
        // Check for impersonation session on mount
        const session = ImpersonationService.getImpersonationSession();
        setImpersonationSession(session);

        // Listen for storage changes (in case impersonation is started/stopped in another tab)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "impersonation_session") {
                const newSession = e.newValue ? JSON.parse(e.newValue) : null;
                setImpersonationSession(newSession);
            }
        };

        // Listen for custom impersonation events
        const handleImpersonationChange = () => {
            const session = ImpersonationService.getImpersonationSession();
            setImpersonationSession(session);
        };

        // EN-1313: a stop postponed until the recorder was idle can still be abandoned if a recording or
        // upload is in progress when it runs. The banner stays up, so say why.
        const handleStopAbandoned = () => {
            toast.error(
                "Impersonation was not stopped because a recording or upload is in progress. Click Stop again when it finishes.",
            );
        };

        window.addEventListener("storage", handleStorageChange);
        window.addEventListener("impersonation-changed", handleImpersonationChange);
        window.addEventListener(IMPERSONATION_STOP_ABANDONED_EVENT, handleStopAbandoned);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener("impersonation-changed", handleImpersonationChange);
            window.removeEventListener(IMPERSONATION_STOP_ABANDONED_EVENT, handleStopAbandoned);
        };
    }, []);

    const handleStopImpersonation = async () => {
        const result = await ImpersonationService.stopImpersonation();
        if (!result.success) {
            // EN-837: a failed stop (e.g. a chunk-load error on the deferred-teardown import after a
            // redeploy) otherwise left a dead button with no feedback — the admin thinks they've stopped
            // but is still impersonating. Surface it so they know to retry / reload.
            console.error("Failed to stop impersonation:", result.error);
            toast.error(
                result.error === RECORDING_IN_OTHER_TAB_ERROR
                    ? result.error
                    : "Couldn't stop impersonation. Please reload and try again.",
            );
            return;
        }
        // EN-837: stopImpersonation now owns the deferred teardown + navigation home (single nav, no
        // race). It dispatches "impersonation-changed" once the recorder is idle, which this banner
        // listens for to hide itself. Don't null the session or navigate here — doing so would hide
        // the banner (and double-navigate) while impersonation is still genuinely active during a
        // deferred teardown mid-recording.
    };

    if (!impersonationSession || !impersonationSession.isImpersonating) {
        return null;
    }

    const getSessionDuration = () => {
        if (!impersonationSession.impersonationStartTime) return "";

        const startTime = new Date(impersonationSession.impersonationStartTime);
        const now = new Date();
        const duration = now.getTime() - startTime.getTime();

        const minutes = Math.floor(duration / 60000);
        const seconds = Math.floor((duration % 60000) / 1000);

        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    return (
        <div className={`bg-yellow-50 border-l-4 border-yellow-400 p-4 ${className}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <AlertTriangleThinIcon className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                            <span className="font-medium">Admin Impersonation Active</span>
                            <br />
                            You are currently impersonating:{" "}
                            <span className="font-semibold">{impersonationSession.targetEmail}</span>
                            <br />
                            <span className="text-xs text-yellow-600">
                                Admin: {impersonationSession.adminEmail} • Duration: {getSessionDuration()}
                            </span>
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleStopImpersonation}
                        className="secondary-button button-sm !text-yellow-700 hover:bg-yellow-100"
                    >
                        Stop Impersonation
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImpersonationBanner;
